import React, { useEffect, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Mail, Send, Reply, Trash2, ArrowLeft, Settings, RefreshCw,
  Inbox, PenSquare, Eye, ChevronLeft, ChevronRight, Lock,
  Server, ShieldCheck, X
} from 'lucide-react';

const emailAPI = {
  getConfig: () => adminAPI._api.get('/api/admin/email/config'),
  saveConfig: (data) => adminAPI._api.post('/api/admin/email/config', data),
  deleteConfig: () => adminAPI._api.delete('/api/admin/email/config'),
  getInbox: (page = 1, limit = 20) => adminAPI._api.get(`/api/admin/email/inbox?page=${page}&limit=${limit}`),
  getMessage: (uid) => adminAPI._api.get(`/api/admin/email/message/${uid}`),
  sendEmail: (data) => adminAPI._api.post('/api/admin/email/send', data),
  replyEmail: (uid, data) => adminAPI._api.post(`/api/admin/email/reply/${uid}`, data),
  deleteMessage: (uid) => adminAPI._api.delete(`/api/admin/email/message/${uid}`),
};

// ==================== CONFIG SETUP ====================
function EmailConfig({ config, onSaved, onCancel }) {
  const [form, setForm] = useState({
    imap_host: config?.imap_host || '',
    imap_port: config?.imap_port || '993',
    smtp_host: config?.smtp_host || '',
    smtp_port: config?.smtp_port || '587',
    email_address: config?.email_address || '',
    password: '',
    use_ssl: config?.use_ssl !== false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.imap_host || !form.smtp_host || !form.email_address || !form.password) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      await emailAPI.saveConfig(form);
      toast.success('Email credentials saved securely');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, field, type = 'text', placeholder }) => (
    <div>
      <label className="text-sm text-gray-400 mb-1 block">{label}</label>
      <Input
        type={type}
        value={form[field]}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        placeholder={placeholder}
        className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white"
        data-testid={`email-config-${field}`}
      />
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="email-config-form">
      <div className="flex items-center gap-3 mb-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} className="text-gray-300" data-testid="email-config-cancel">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        )}
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
          <h2 className="text-xl font-bold text-white">Email Configuration</h2>
        </div>
      </div>

      <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-[hsl(var(--border))]">
            <Lock className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-400">Credentials are encrypted with AES-256 before storage</span>
          </div>

          <Field label="Email Address" field="email_address" placeholder="you@domain.com" />
          <Field label="Password / App Password" field="password" type="password" placeholder="Enter password" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="IMAP Host" field="imap_host" placeholder="imap.domain.com" />
            <Field label="IMAP Port" field="imap_port" placeholder="993" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="SMTP Host" field="smtp_host" placeholder="smtp.domain.com" />
            <Field label="SMTP Port" field="smtp_port" placeholder="587" />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.use_ssl}
              onChange={(e) => setForm({ ...form, use_ssl: e.target.checked })}
              className="rounded"
              id="use_ssl"
              data-testid="email-config-ssl"
            />
            <label htmlFor="use_ssl" className="text-sm text-gray-400">Use SSL/TLS for IMAP</label>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} className="border-[hsl(var(--border))] text-gray-300">Cancel</Button>
        )}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))]"
          data-testid="email-config-save"
        >
          <Lock className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save & Encrypt'}
        </Button>
      </div>
    </div>
  );
}

// ==================== COMPOSE / REPLY ====================
function ComposeEmail({ replyTo, onSent, onCancel, fromAddress }) {
  const [to, setTo] = useState(replyTo?.from || '');
  const [subject, setSubject] = useState(
    replyTo ? (replyTo.subject.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject}`) : ''
  );
  const [body, setBody] = useState(
    replyTo ? `\n\n--- Original Message ---\nFrom: ${replyTo.from}\nDate: ${replyTo.date}\nSubject: ${replyTo.subject}\n\n${replyTo.body_text || ''}` : ''
  );
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      toast.error('To, subject, and body are required');
      return;
    }
    setSending(true);
    try {
      if (replyTo?.uid) {
        await emailAPI.replyEmail(replyTo.uid, { body: body.trim() });
      } else {
        await emailAPI.sendEmail({ to: to.trim(), subject: subject.trim(), body: body.trim() });
      }
      toast.success('Email sent!');
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="email-compose">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onCancel} className="text-gray-300" data-testid="email-compose-cancel">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button
          onClick={handleSend}
          disabled={sending}
          className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))]"
          data-testid="email-send-btn"
        >
          <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send'}
        </Button>
      </div>

      <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 w-16">From:</span>
            <span className="text-sm text-gray-300">{fromAddress}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 w-16">To:</span>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-sm"
              data-testid="email-compose-to"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 w-16">Subject:</span>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-sm"
              data-testid="email-compose-subject"
            />
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message..."
            rows={14}
            className="w-full bg-[hsl(var(--accent))] border border-[hsl(var(--border))] rounded-lg p-4 text-gray-200 placeholder:text-gray-600 resize-y text-sm leading-relaxed focus:outline-none focus:border-[hsl(var(--brand-gold-500))]/50"
            data-testid="email-compose-body"
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== EMAIL READER ====================
function EmailReader({ uid, onBack, onReply, onDelete }) {
  const [emailData, setEmailData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await emailAPI.getMessage(uid);
        setEmailData(res.data.email);
      } catch (err) {
        toast.error('Failed to load email');
        onBack();
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [uid, onBack]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await emailAPI.deleteMessage(uid);
      toast.success('Email deleted');
      onDelete();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="text-gray-400 text-center py-12">Loading email...</div>;
  if (!emailData) return null;

  return (
    <div className="space-y-4" data-testid="email-reader">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-gray-300 self-start" data-testid="email-reader-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Inbox
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onReply(emailData)} className="border-[hsl(var(--border))] text-gray-300" data-testid="email-reply-btn">
            <Reply className="w-4 h-4 mr-1 sm:mr-2" /> Reply
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            disabled={deleting}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid="email-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-1 sm:mr-2" /> Delete
          </Button>
        </div>
      </div>

      <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
        <CardContent className="p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-4" data-testid="email-reader-subject">{emailData.subject || '(No Subject)'}</h2>
          <div className="flex flex-col gap-1 text-xs sm:text-sm text-gray-400 mb-6 pb-4 border-b border-[hsl(var(--border))]">
            <div className="break-all"><span className="text-gray-500">From:</span> <span className="text-gray-300">{emailData.from}</span></div>
            <div className="break-all"><span className="text-gray-500">To:</span> <span className="text-gray-300">{emailData.to}</span></div>
            <div><span className="text-gray-500">Date:</span> <span className="text-gray-300">{emailData.date}</span></div>
          </div>

          {emailData.body_html ? (
            <div
              className="prose-email text-gray-300 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailData.body_html, { USE_PROFILES: { html: true } }) }}
              data-testid="email-reader-body"
            />
          ) : (
            <pre className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap font-sans" data-testid="email-reader-body">
              {emailData.body_text || '(No content)'}
            </pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== INBOX LIST ====================
function InboxView({ onOpenEmail, onCompose, onSettings }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const loadInbox = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailAPI.getInbox(page, limit);
      setEmails(res.data.emails || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { loadInbox(); }, [loadInbox]);

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      if (d.toDateString() === now.toDateString()) {
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      }
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr.substring(0, 16);
    }
  };

  return (
    <div className="space-y-4" data-testid="email-inbox">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Inbox className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
          <h2 className="text-lg sm:text-xl font-bold text-white">Inbox</h2>
          <Badge className="bg-[hsl(var(--accent))] text-gray-300 border-[hsl(var(--border))]">{total}</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadInbox} className="border-[hsl(var(--border))] text-gray-300" data-testid="email-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={onSettings} className="border-[hsl(var(--border))] text-gray-300" data-testid="email-settings-btn">
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            onClick={onCompose}
            className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))]"
            data-testid="email-compose-btn"
          >
            <PenSquare className="w-4 h-4 mr-1 sm:mr-2" /> Compose
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-400 text-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading emails...
        </div>
      ) : emails.length === 0 ? (
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-12 text-center">
            <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Inbox is empty</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-1">
            {emails.map((em, idx) => (
              <button
                key={`${em.uid}-${idx}`}
                onClick={() => onOpenEmail(em.uid)}
                className={`w-full text-left p-3 rounded-lg border transition-all hover:border-[hsl(var(--brand-gold-500))]/40 ${
                  em.is_read
                    ? 'bg-[hsl(var(--secondary))] border-[hsl(var(--border))]'
                    : 'bg-[hsl(var(--secondary))] border-[hsl(var(--brand-gold-500))]/20'
                }`}
                data-testid={`email-row-${em.uid}`}
              >
                <div className="flex items-center gap-3">
                  {!em.is_read && <div className="w-2 h-2 rounded-full bg-[hsl(var(--brand-gold-500))] flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm truncate ${em.is_read ? 'text-gray-400' : 'text-white font-medium'}`}>
                        {em.from}
                      </span>
                      <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(em.date)}</span>
                    </div>
                    <p className={`text-sm truncate mt-0.5 ${em.is_read ? 'text-gray-500' : 'text-gray-300'}`}>
                      {em.subject || '(No Subject)'}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-[hsl(var(--border))] text-gray-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-[hsl(var(--border))] text-gray-300"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== EMAIL TAB (MAIN) ====================
export default function EmailTab() {
  const [configStatus, setConfigStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('inbox'); // inbox | config | read | compose
  const [selectedUid, setSelectedUid] = useState(null);
  const [replyData, setReplyData] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const res = await emailAPI.getConfig();
      setConfigStatus(res.data);
    } catch {
      setConfigStatus({ configured: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  if (loading) return <div className="text-gray-400 text-center py-12">Loading...</div>;

  // Not configured — show setup
  if (!configStatus?.configured && view !== 'config') {
    return (
      <div className="max-w-2xl mx-auto text-center py-12" data-testid="email-not-configured">
        <Server className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">Email Agent Setup</h2>
        <p className="text-gray-400 mb-6">Connect your email account to read, reply, and compose emails directly from the admin panel.</p>
        <div className="flex items-center justify-center gap-2 text-sm text-emerald-400 mb-6">
          <ShieldCheck className="w-4 h-4" />
          <span>Credentials are encrypted with AES-256</span>
        </div>
        <Button
          onClick={() => setView('config')}
          className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))]"
          data-testid="email-setup-btn"
        >
          <Mail className="w-4 h-4 mr-2" /> Configure Email
        </Button>
      </div>
    );
  }

  if (view === 'config') {
    return (
      <EmailConfig
        config={configStatus?.configured ? configStatus : null}
        onSaved={() => { loadConfig(); setView('inbox'); }}
        onCancel={configStatus?.configured ? () => setView('inbox') : null}
      />
    );
  }

  if (view === 'read' && selectedUid) {
    return (
      <EmailReader
        uid={selectedUid}
        onBack={() => { setSelectedUid(null); setView('inbox'); }}
        onReply={(emailData) => { setReplyData(emailData); setView('compose'); }}
        onDelete={() => { setSelectedUid(null); setView('inbox'); }}
      />
    );
  }

  if (view === 'compose') {
    return (
      <ComposeEmail
        replyTo={replyData}
        fromAddress={configStatus?.email_address || ''}
        onSent={() => { setReplyData(null); setView('inbox'); }}
        onCancel={() => { setReplyData(null); setView('inbox'); }}
      />
    );
  }

  // Inbox
  return (
    <InboxView
      onOpenEmail={(uid) => { setSelectedUid(uid); setView('read'); }}
      onCompose={() => { setReplyData(null); setView('compose'); }}
      onSettings={() => setView('config')}
    />
  );
}
